import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { sendEmail, complianceDocPublishedEmail } from '../lib/email';
import { fetchActivePortalUsersBySupplier } from '../lib/briefRecipients';
import NotifySuppliersModal from './NotifySuppliersModal';
import ConfirmDialog from './ConfirmDialog';
import { useActionToast } from './ActionToast';
import { fmtDate } from '../lib/dates';
import { isBuilt as isFormBuilt } from '../lib/hostedForm';

// Risk Flag Configuration matrix — admin surface for managing which
// compliance documents apply to which supplier risk tier, and (via the
// per-row drawer) the validity / file-type / reminder-rule / enforcement
// config for each document.
//
// Data model:
//   - fusion_documents: one row per compliance document type. The
//     `compliance_doc_key` is the join key into supplier_onboarding_documents
//     (per-supplier instances) and risk_document_requirements (per-tier
//     requirement flags).
//   - risk_document_requirements(risk_level, document_key): a row's
//     presence means "this doc is required for that tier".
//   - document_reminder_rules: child of fusion_documents (FK by
//     compliance_doc_key). One row per reminder schedule. Cron iterates
//     these to decide who to email.
//
// All state — docs, rules, tier requirements, search, filter, drawer mode
// — lives inside this component. Admin.js mounts it once when the tab is
// active and otherwise stays out of its business.

// Categories are now data-driven: the 'required'-behaviour rows in
// document_categories (managed in Fusion Documents) are the compliance
// categories. Documents group by their document_type (the category key).
// Neutral tone for the little category badge — per-category colours retired
// with the old hardcoded taxonomy.
const CATEGORY_TONE_NEUTRAL = { color: '#2E8FA1', border: 'rgba(46, 143, 161, 0.3)' };
const TIER_COLOR = { high: '#E0290A', medium: '#E89A2E', low: '#92D400' };

// Format a validity period for the "Expires After" column.
const formatValidity = (value, unit) => {
  if (value == null || value === '') return null;
  return `${value} ${unit}`;
};

export default function RiskFlagMatrix({ currentUser }) {
  const [docs, setDocs] = useState([]);
  const [rulesByKey, setRulesByKey] = useState({});      // { compliance_doc_key: [rule, ...] }
  const [riskRequirements, setRiskRequirements] = useState(new Set());
  const [requiredCategories, setRequiredCategories] = useState([]); // document_categories, behaviour='required'
  const [loaded, setLoaded] = useState(false);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  const [savingTickKey, setSavingTickKey] = useState(null);
  const [notifyingDocKey, setNotifyingDocKey] = useState(null);

  const [restoreConfirm, setRestoreConfirm] = useState(null); // doc being restored
  const [notifyPicker, setNotifyPicker] = useState(null);     // { doc, suppliers, usersBySupplier } when open

  // Shared action toast (unified 2026-08-07); flash() delegates, call sites unchanged.
  const { notify, Toast } = useActionToast();
  const flash = useCallback(
    (msg, tone = 'green') => notify({ title: msg, tone: tone === 'red' ? 'danger' : 'success' }, { holdMs: tone === 'red' ? 6000 : 4000 }),
    [notify],
  );

  // ============== Loaders ==============

  const loadAll = useCallback(async () => {
    const [{ data: docsData }, { data: rulesData }, { data: reqs }, { data: cats }] = await Promise.all([
      supabase
        .from('fusion_documents')
        .select('*')
        .not('compliance_doc_key', 'is', null)
        .order('document_name'),
      supabase.from('document_reminder_rules').select('*'),
      supabase.from('risk_document_requirements').select('risk_level, document_key'),
      supabase.from('document_categories').select('key, label, sort_order').eq('behaviour', 'required').order('sort_order'),
    ]);
    // Drop unpublished EDITS of a live document (versioning §3a): they share
    // their parent's compliance_doc_key, so leaving them in would duplicate the
    // row and — worse — let a tier tick "publish" a draft that was only ever an
    // in-progress edit. Standalone drafts (never published) still belong here:
    // ticking a tier is how they go live.
    setDocs((docsData || []).filter(d => !(d.status === 'draft' && d.parent_document_id)));
    setRequiredCategories(cats || []);
    const grouped = {};
    for (const r of (rulesData || [])) {
      if (!grouped[r.document_key]) grouped[r.document_key] = [];
      grouped[r.document_key].push(r);
    }
    setRulesByKey(grouped);
    setRiskRequirements(new Set((reqs || []).map(r => `${r.risk_level}:${r.document_key}`)));
    setLoaded(true);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ============== Matrix tick toggle (optimistic write) ==============
  // Lifts the existing Admin.js logic verbatim — risk_document_requirements
  // is a thin presence/absence table, so a tick = insert and an untick =
  // delete on (risk_level, document_key).

  const toggleRiskRequirement = async (riskLevel, documentKey) => {
    const key = `${riskLevel}:${documentKey}`;
    // Refuse to assign a hosted form that hasn't been built — it has no content
    // to show suppliers yet. Unticking (removing) is always allowed.
    if (!riskRequirements.has(key)) {
      const target = docs.find(d => d.compliance_doc_key === documentKey);
      if (target && (target.satisfied_by || '') === 'form' && !isFormBuilt(target.form_schema)) {
        flash('Build this form before assigning it to a tier — it has no content yet.', 'red');
        return;
      }
    }
    setSavingTickKey(key);
    if (riskRequirements.has(key)) {
      const { error: delError } = await supabase
        .from('risk_document_requirements')
        .delete()
        .eq('risk_level', riskLevel)
        .eq('document_key', documentKey);
      if (delError) {
        flash(`Could not update requirement: ${delError.message}`, 'red');
      } else {
        setRiskRequirements(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        flash('Saved');
      }
    } else {
      const { error: insError } = await supabase
        .from('risk_document_requirements')
        .insert([{ risk_level: riskLevel, document_key: documentKey }]);
      if (insError) {
        flash(`Could not update requirement: ${insError.message}`, 'red');
      } else {
        setRiskRequirements(prev => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        // Assigning a tier publishes a compliance draft — it becomes a live
        // requirement for that tier's suppliers (the supplier list excludes
        // drafts). No-op for docs already active.
        const doc = docs.find(d => d.compliance_doc_key === documentKey);
        if (doc && doc.status === 'draft') {
          // Must check the publish error: if this UPDATE silently fails (RLS /
          // a stale CHECK constraint), the tier tick still lands but the doc
          // stays draft in the DB — so the matrix reads "published" while the
          // supplier portal (which excludes drafts) never shows it. Surface it
          // instead of optimistically flipping local state.
          const { error: pubErr } = await supabase.from('fusion_documents')
            .update({ status: 'active', is_active: true })
            .eq('id', doc.id);
          if (pubErr) {
            flash(`Tier saved, but publishing the document failed — it's still a draft and won't show to suppliers: ${pubErr.message}`, 'red');
          } else {
            setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, status: 'active', is_active: true } : d));
            flash('Published & required for this tier');
          }
        } else {
          flash('Saved');
        }
      }
    }
    setSavingTickKey(null);
  };

  // Document creation + configuration now live entirely in Fusion Documents.
  // The matrix is assignment-only: tick which tiers require a doc + Notify.
  // Restore (below) stays so archived compliance docs can be reinstated here.

  const handleRestore = async (doc) => {
    setRestoreConfirm(null);
    const { error: restErr } = await supabase
      .from('fusion_documents')
      .update({ is_active: true, archived_at: null })
      .eq('id', doc.id);
    if (restErr) {
      flash(`Could not restore: ${restErr.message}`, 'red');
      return;
    }
    await loadAll();
    flash(`"${doc.document_name}" restored`);
  };

  // ============== Notify Suppliers (preserved from Admin.js) ==============
  // Reach-out fan-out for a single doc: find every active supplier whose
  // risk_flag matches a level this doc is ticked for, then email main +
  // secondary contacts and log one supplier_email_log row each.

  // Open the notify picker: gather the tier-matched suppliers (those whose
  // risk_flag matches a level this doc is ticked for) that have a portal user.
  const openNotify = async (doc) => {
    const levels = ['high', 'medium', 'low'].filter(level =>
      riskRequirements.has(`${level}:${doc.compliance_doc_key}`)
    );
    if (levels.length === 0) {
      flash(`"${doc.document_name}" isn't ticked for any risk level — tick it for at least one tier first.`, 'red');
      return;
    }
    const { data: targets, error: queryError } = await supabase
      .from('suppliers')
      .select('id, supplier_name')
      .eq('status', 'active')
      .eq('is_record_only', false)
      .in('risk_flag', levels)
      .order('supplier_name');
    if (queryError) {
      flash(`Could not load suppliers: ${queryError.message}`, 'red');
      return;
    }
    const targetList = targets || [];
    // Route to portal users (supplier_users) — the same recipients every brief
    // email uses — not the stored main/secondary contacts.
    const usersBySupplier = await fetchActivePortalUsersBySupplier(targetList.map(s => s.id));
    const suppliers = targetList
      .map(s => ({ id: s.id, supplier_name: s.supplier_name, userCount: (usersBySupplier.get(s.id) || []).length }))
      .filter(s => s.userCount > 0);
    if (suppliers.length === 0) {
      flash(`No active ${levels.join(' / ')} risk suppliers have an active portal user to email.`, 'red');
      return;
    }
    setNotifyPicker({ doc, suppliers, usersBySupplier, levels });
  };

  // Fan out to the chosen suppliers (null = everyone in the picker).
  const sendNotify = async (selectedIds) => {
    const picker = notifyPicker;
    if (!picker) return;
    const { doc, suppliers, usersBySupplier } = picker;
    const chosen = Array.isArray(selectedIds) ? suppliers.filter(s => selectedIds.includes(s.id)) : suppliers;
    setNotifyPicker(null);
    setNotifyingDocKey(doc.compliance_doc_key);
    const portalUrl = `${window.location.origin}/portal`;
    let emailsSent = 0;
    let suppliersLogged = 0;
    const failures = [];

    for (const supplier of chosen) {
      const users = usersBySupplier.get(supplier.id) || [];
      const sentTo = [];
      for (const u of users) {
        const { subject, html } = complianceDocPublishedEmail({
          contactName: u.full_name,
          supplierName: supplier.supplier_name,
          documentLabel: doc.document_name,
          portalUrl,
        });
        try {
          await sendEmail({ to: u.email, subject, html });
          emailsSent++;
          sentTo.push(u.email);
        } catch (e) {
          failures.push(`${supplier.supplier_name} → ${u.email}`);
        }
      }
      if (sentTo.length > 0) {
        const { error: logError } = await supabase.from('supplier_email_log').insert([{
          supplier_id: supplier.id,
          email_kind: 'compliance_doc_published',
          recipients: sentTo,
          sent_by: currentUser?.id || null,
        }]);
        if (!logError) suppliersLogged++;
      }
    }

    setNotifyingDocKey(null);
    if (failures.length > 0) {
      flash(`Notified ${suppliersLogged} supplier(s) — ${emailsSent} email(s) sent, ${failures.length} failed.`, 'red');
    } else {
      flash(`Notified ${suppliersLogged} supplier(s) about "${doc.document_name}" — ${emailsSent} email(s) sent.`);
    }
  };

  // ============== Derived view data ==============

  // Category = the document's document_type (a document_categories key). Label
  // + display order come from the required categories loaded above; any stray
  // document_type not among them is appended so no doc is hidden.
  const categoryByKey = useMemo(() => new Map(requiredCategories.map(c => [c.key, c])), [requiredCategories]);
  const labelFor = useCallback((key) => categoryByKey.get(key)?.label || key || 'Uncategorised', [categoryByKey]);
  const orderedCategoryKeys = useMemo(() => {
    const keys = requiredCategories.map(c => c.key);
    const seen = new Set(keys);
    for (const d of docs) {
      const k = d.document_type || 'compliance';
      if (!seen.has(k)) { seen.add(k); keys.push(k); }
    }
    return keys;
  }, [requiredCategories, docs]);

  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter(d => {
      if (!q) return true;
      const hay = `${d.document_name || ''} ${labelFor(d.document_type)}`.toLowerCase();
      return hay.includes(q);
    });
  }, [docs, search, labelFor]);

  const activeByCategory = useMemo(() => {
    const map = {};
    for (const d of filteredDocs) {
      if (!d.is_active) continue;
      const cat = d.document_type || 'compliance';
      if (categoryFilter !== 'all' && cat !== categoryFilter) continue;
      if (!map[cat]) map[cat] = [];
      map[cat].push(d);
    }
    return map;
  }, [filteredDocs, categoryFilter]);

  const archivedDocs = useMemo(() => filteredDocs.filter(d => !d.is_active), [filteredDocs]);

  return (
    <div>
      {/* ============ Section header ============ */}
      <div className="flex justify-between items-end gap-4 mb-5">
        <div>
          <h3 className="font-['Metropolis'] text-base font-bold tracking-[0.18em] uppercase text-ink">
            Risk Flag Configuration
          </h3>
          <p className="font-['Metropolis'] text-xs text-[#51534A] mt-1 max-w-[720px] leading-relaxed">
            Set which compliance documents are required for each supplier risk level. Documents and
            their config (expiry, accepted file types, reminder rules) are created and edited in
            <strong className="text-fusion-forest"> Fusion Documents</strong>.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowArchived(prev => !prev)}
            className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase bg-white border border-line rounded px-4 py-2 text-ink hover:border-[#DADCCD] transition-colors">
            {showArchived ? 'Hide Archived' : 'View Archived'}
          </button>
        </div>
      </div>

      {/* ============ Risk-tier reference ============ */}
      <div className="bg-white border border-line rounded-lg p-4 mb-4 grid grid-cols-[auto_1fr_1fr_1fr] gap-6 items-center">
        <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] leading-snug">
          Risk Tiers
        </div>
        <TierItem color="#E0290A" name="High Risk" sub="Working at height, electrical, high-value installations" />
        <TierItem color="#E89A2E" name="Medium Risk" sub="Catering, AV, on-site activations" />
        <TierItem color="#92D400" name="Low Risk" sub="Florals, props, talent, retail" />
      </div>

      {/* ============ Toolbar ============ */}
      <div className="flex gap-2.5 items-center mb-3.5 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search documents..."
          className="flex-1 max-w-[340px] bg-white border border-line rounded px-3.5 py-2 text-ink font-['Metropolis'] text-[13px] outline-none focus:border-[#DADCCD] transition-colors" />
        <FilterPill
          active={categoryFilter === 'all'}
          onClick={() => setCategoryFilter('all')}>All Categories</FilterPill>
        {orderedCategoryKeys.map(cat => (
          <FilterPill
            key={cat}
            active={categoryFilter === cat}
            onClick={() => setCategoryFilter(cat)}>
            {labelFor(cat)}
          </FilterPill>
        ))}
      </div>

      {/* ============ Matrix ============ */}
      {!loaded ? (
        <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading…</p>
      ) : filteredDocs.length === 0 && categoryFilter === 'all' && !search ? (
        <p className="font-['Metropolis'] text-sm text-[#51534A]">
          No compliance documents yet. Create them in <strong className="text-fusion-forest">Fusion Documents</strong> (a category with the <strong className="text-fusion-forest">Required</strong> behaviour), then tick which risk tiers need them here.
        </p>
      ) : (
        <div className="bg-white border border-line rounded-lg overflow-hidden">
          <MatrixHeader />
          {orderedCategoryKeys.map(cat => {
            const list = activeByCategory[cat];
            if (!list || list.length === 0) return null;
            return (
              <React.Fragment key={cat}>
                <CategoryDivider label={labelFor(cat)} count={list.length} />
                {list.map(doc => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    categoryLabel={labelFor(doc.document_type)}
                    rulesCount={(rulesByKey[doc.compliance_doc_key] || []).length}
                    riskRequirements={riskRequirements}
                    savingTickKey={savingTickKey}
                    isNotifying={notifyingDocKey === doc.compliance_doc_key}
                    notifyDisabled={notifyingDocKey !== null && notifyingDocKey !== doc.compliance_doc_key}
                    onTickToggle={toggleRiskRequirement}
                    onNotify={() => openNotify(doc)} />
                ))}
              </React.Fragment>
            );
          })}

          {/* No active match in chosen category */}
          {Object.keys(activeByCategory).length === 0 && (search || categoryFilter !== 'all') && (
            <div className="px-5 py-8 text-center font-['Metropolis'] text-sm text-[#51534A]">
              No documents match this filter.
            </div>
          )}

          {/* ============ Archived ============ */}
          {showArchived && archivedDocs.length > 0 && (
            <>
              <CategoryDivider label="Archived" count={archivedDocs.length} archived />
              {archivedDocs.map(doc => (
                <ArchivedRow
                  key={doc.id}
                  doc={doc}
                  onRestore={() => setRestoreConfirm(doc)} />
              ))}
            </>
          )}
        </div>
      )}

      {/* ============ Toast ============ */}
      <Toast />

      {/* ============ Restore confirm ============ */}
      <ConfirmDialog
        open={!!restoreConfirm}
        title={restoreConfirm ? `Restore "${restoreConfirm.document_name}"?` : ''}
        body="It'll re-appear in the active matrix. Risk-tier requirements and reminder rules are preserved from before it was archived."
        confirmLabel="Restore"
        cancelLabel="Cancel"
        onConfirm={() => restoreConfirm && handleRestore(restoreConfirm)}
        onCancel={() => setRestoreConfirm(null)} />

      {/* ============ Notify picker ============ */}
      <NotifySuppliersModal
        open={!!notifyPicker}
        title="Notify suppliers"
        intro={notifyPicker
          ? `Email suppliers about "${notifyPicker.doc.document_name}". Only ${notifyPicker.levels.map(l => l.toUpperCase()).join(' / ')}-risk suppliers with a portal user are eligible. Each send is logged to the supplier's Activity tab.`
          : ''}
        suppliers={notifyPicker?.suppliers || []}
        busy={notifyingDocKey !== null}
        confirmLabel="Notify"
        onConfirm={(ids) => sendNotify(ids)}
        onCancel={() => setNotifyPicker(null)} />

    </div>
  );
}

// =========================== sub-components ===========================

function TierItem({ color, name, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
      <div>
        <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-ink">{name}</div>
        <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-tight">{sub}</div>
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded border transition-colors ${active
        ? 'text-fusion-forest border-[#92D400] bg-[#92D400]/[0.06]'
        : 'text-[#51534A] border-line bg-white hover:text-ink hover:border-[#DADCCD]'}`}>
      {children}
    </button>
  );
}

const ROW_GRID = 'grid grid-cols-[1fr_130px_130px_130px_90px_180px] items-center gap-0 px-5';

function MatrixHeader() {
  return (
    <div className={`${ROW_GRID} py-3.5 bg-white/[0.02] border-b border-line font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A]`}>
      <div>Document</div>
      <div className="text-center text-[#E0290A]">High Risk</div>
      <div className="text-center text-[#7A4E10]">Medium Risk</div>
      <div className="text-center text-fusion-forest">Low Risk</div>
      <div className="text-center">Expires After</div>
      <div className="text-right">Actions</div>
    </div>
  );
}

function CategoryDivider({ label, count, archived }) {
  return (
    <div
      className={`px-5 py-3 font-['Metropolis'] text-[10px] font-bold tracking-[0.25em] uppercase flex items-center gap-2.5 ${archived
        ? 'bg-[#51534A]/[0.08] text-[#51534A] border-t border-b border-line'
        : 'bg-[#92D400]/[0.04] text-fusion-forest border-t border-b border-[#92D400]/[0.15]'}`}>
      <span>{label}</span>
      <span className={`text-[9px] tracking-[0.15em] px-2 py-0.5 rounded-full ${archived
        ? 'bg-[#51534A]/[0.15] text-[#51534A]'
        : 'bg-[#92D400]/10 text-fusion-forest'}`}>
        {count} {count === 1 ? 'document' : 'documents'}
      </span>
    </div>
  );
}

function DocumentRow({
  doc, categoryLabel, rulesCount, riskRequirements, savingTickKey,
  isNotifying, notifyDisabled, onTickToggle, onNotify,
}) {
  const tone = CATEGORY_TONE_NEUTRAL;
  const validity = formatValidity(doc.validity_value, doc.validity_unit);
  const tickedForAnyTier = ['high', 'medium', 'low'].some(level =>
    riskRequirements.has(`${level}:${doc.compliance_doc_key}`)
  );
  return (
    <div
      className={`${ROW_GRID} py-4 border-b border-line last:border-b-0 transition-colors hover:bg-white/[0.015]`}>
      {/* Doc cell — config now lives in Fusion Documents, so this is read-only. */}
      <div className="min-w-0 text-left">
        <div className="font-['Metropolis'] text-[13px] font-bold text-ink mb-1 truncate">
          {doc.document_name}
        </div>
        <div className="font-['Metropolis'] text-[10px] text-[#51534A] flex gap-2 items-center flex-wrap">
          <span
            className="inline-flex px-1.5 py-0.5 rounded font-bold text-[9px] tracking-[0.15em] uppercase bg-white border"
            style={{ color: tone.color, borderColor: tone.border }}>
            {categoryLabel || 'Uncategorised'}
          </span>
          {doc.description && (
            <>
              <span className="w-[3px] h-[3px] bg-[#51534A] rounded-full" />
              <span className="truncate">{doc.description}</span>
            </>
          )}
          {rulesCount > 0 && (
            <>
              <span className="w-[3px] h-[3px] bg-[#51534A] rounded-full" />
              <span>{rulesCount} reminder {rulesCount === 1 ? 'rule' : 'rules'}</span>
            </>
          )}
          {!doc.file_url && (
            <>
              <span className="w-[3px] h-[3px] bg-[#51534A] rounded-full" />
              <span className="text-[#7A4E10]">No file uploaded yet</span>
            </>
          )}
        </div>
      </div>

      {/* Tier ticks */}
      {['high', 'medium', 'low'].map(level => {
        const key = `${level}:${doc.compliance_doc_key}`;
        const checked = riskRequirements.has(key);
        const saving = savingTickKey === key;
        const color = TIER_COLOR[level];
        return (
          <div key={level} className="flex justify-center">
            <button
              type="button"
              onClick={() => onTickToggle(level, doc.compliance_doc_key)}
              disabled={saving}
              aria-label={`${checked ? 'Remove' : 'Add'} ${doc.document_name} requirement for ${level} risk suppliers`}
              className="w-[22px] h-[22px] rounded-[3px] border-2 flex items-center justify-center transition-colors disabled:opacity-50"
              style={checked
                ? { background: color, borderColor: color }
                : { background: 'transparent', borderColor: '#C7CFB4' }}>
              {checked && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={level === 'high' ? '#F9FCF1' : '#131313'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          </div>
        );
      })}

      {/* Expires after */}
      <div className="text-center">
        {validity
          ? <span className="font-['Metropolis'] text-xs text-ink font-semibold">{validity}</span>
          : <span className="font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase text-[#51534A]">Never</span>}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-1.5">
        <button
          type="button"
          onClick={onNotify}
          disabled={!tickedForAnyTier || isNotifying || notifyDisabled}
          title={tickedForAnyTier
            ? 'Email every active supplier whose risk flag matches a level this doc is ticked for.'
            : 'Tick the box for at least one risk level to enable.'}
          className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tickedForAnyTier
            ? 'text-[#2E8FA1] border-[#2E8FA1]/30 bg-transparent hover:bg-[#2E8FA1]/[0.06] hover:border-[#2E8FA1]'
            : 'text-[#51534A] border-line bg-transparent'}`}>
          {isNotifying ? 'Sending…' : 'Notify'}
        </button>
      </div>
    </div>
  );
}

function ArchivedRow({ doc, onRestore }) {
  return (
    <div className={`${ROW_GRID} py-4 border-b border-line last:border-b-0 opacity-50`}>
      <div className="min-w-0">
        <div className="font-['Metropolis'] text-[13px] font-bold text-ink mb-1 flex items-center gap-2">
          <span className="truncate">{doc.document_name}</span>
          <span className="bg-[#51534A]/20 text-[#51534A] px-1.5 py-0.5 rounded font-bold text-[9px] tracking-[0.15em] uppercase">Archived</span>
        </div>
        <div className="font-['Metropolis'] text-[10px] text-[#51534A]">
          No longer required for new suppliers · {doc.archived_at ? `archived ${fmtDate(doc.archived_at)}` : 'archived'}
        </div>
      </div>
      {['high', 'medium', 'low'].map(level => (
        <div key={level} className="flex justify-center">
          <div className="w-[22px] h-[22px] rounded-[3px] border-2 border-line opacity-30" />
        </div>
      ))}
      <div className="text-center">
        <span className="font-['Metropolis'] text-[10px] tracking-[0.15em] uppercase text-[#51534A]">N/A</span>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onRestore}
          className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1.5 rounded border border-line text-[#51534A] bg-transparent hover:text-ink hover:border-[#DADCCD] transition-colors">
          Restore
        </button>
      </div>
    </div>
  );
}
